import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleRight, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import classNames from 'classnames';
import Link from 'next/link';
import { CSSProperties, ReactNode, createRef, useContext, useEffect, useRef } from 'react';
import { YadoDetailJson } from '../../types/response_types/YadoResponseTypes';
import { CurrencyCode } from '../../legacy/types/ExchangeRates';
import YadoPrice from '../../models/mobx/YadoPrice';
import NextImage from '../common/NextImage';
import i18n from '../../R/string/i18n';
import { createOnsenYadoDetailLink } from '../../shared/utils/url_helper';
import SearchCondition from '../../models/search/SearchCondition';
import { evaluationLabel } from '../../helpers/yado_helper';
import PriceJpy from '../onsen_yado/PriceJpy';
import { SoldOut } from '../onsen_yado/OnsenYadoSearchResult';
import Price from '../onsen_yado/Price';
import { DeviceContext } from '../../models/context/DeviceContext';
import CouponStore from '../../models/mobx/stores/CouponStore';
import R from '../../R';
import { Breakpoints, Colors, convertBreakpoint } from '../../shared/styles/StyleConst';
import { ActivePointContent } from '../../models/search/WamazingModel';
import ImageLazy from '../common/ImageLazy';

export const OpenMapPCButton = ({ onClick }: { onClick: () => void }) => {
  if (window.innerWidth <= convertBreakpoint(Breakpoints.xsMax)) {
    return null;
  }
  return (
    <>
      <div className="map-button-container">
        <button className="map-button" type="button" onClick={onClick}>
          {i18n.viewOnMap()}
          <span>
            <FontAwesomeIcon
              icon={faAngleRight}
              style={{
                color: Colors.textDarkGray,
                fontSize: 15,
                marginLeft: 10,
              }}
            />
          </span>
        </button>
      </div>
      <style jsx>{`
        .map-button-container {
          position: relative;
          z-index: 1;
          border: 1px solid ${Colors.pjtBorder};
          display: flex;
          height: 53px;
          justify-content: center;
          align-items: center;
        }
        .map-button-container:after {
          content: '';
          position: absolute;
          z-index: -1;
          top: 0;
          bottom: 0;
          left: 0;
          right: 0;
          background-image: url(${R.assets.images.commonCityMap});
          opacity: 0.5;
        }
        .map-button {
          font-weight: 700;
          background-color: transparent;
          border: none;
          color: ${Colors.textDarkGray};
          outline: 0;
        }
      `}</style>
    </>
  );
};

export const OpenMapMobileButton = ({ onClick }: { onClick: () => void }) => {
  if (window.innerWidth > convertBreakpoint(Breakpoints.xsMax)) {
    return null;
  }
  return (
    <>
      <div className="map-button-container">
        <button className="map-button" type="button" onClick={onClick}>
          {i18n.viewOnMap()}
          <FontAwesomeIcon
            icon={faChevronRight}
            size="sm"
            color={Colors.pjtBlue}
            className="modal-open-btn-chevron"
          />
        </button>
      </div>
      <style jsx>{`
        .map-button-container {
          display: flex;
          width: 100%;
          justify-content: flex-end;
        }
        .map-button {
          background-color: transparent;
          padding: 10px 20px;
          color: ${Colors.pjtBlue};
          outline: 0;
          border: 0;
        }
      `}</style>
    </>
  );
};

export const CloseMapButton = ({
  onClick,
  buttonStyle,
  customText,
}: {
  onClick: () => void;
  buttonStyle?: CSSProperties;
  customText?: string;
}) => (
  <>
    <div className="list-button-container">
      <button className="list-button" type="button" onClick={onClick} style={buttonStyle}>
        <span>{customText || i18n.backToList()}</span>
        <span>X</span>
      </button>
    </div>
    <style jsx>{`
      .list-button-container {
        position: relative;
      }
      .list-button {
        position: absolute;
        right: 20px;
        top: 20px;
        z-index: 500;
        display: flex;
        padding: 8px 20px;
        height: 39px;
        justify-content: center;
        align-items: center;
        gap: 10px;
        outline: 0;
        border-radius: 3px;
        border: 1px solid ${Colors.gray300};
        background-color: ${Colors.onsenWhite};
      }
      @media screen and (max-width: ${Breakpoints.xsMax}) {
        .list-button {
          top: 70px;
        }
      }
    `}</style>
  </>
);

export const YadosMapTooltip = ({
  currency = 'JPY',
  price,
}: {
  price?: number | null;
  currency?: CurrencyCode;
}) => (
  <>
    <div className="tooltip-container">
      <span className="tooltip-text">
        {price
          ? `${(Math.floor(price / 1000) / 10).toFixed(1)}${i18n.webYadoTenThousand()} ${currency}`
          : i18n.fullyOccupied()}
      </span>
    </div>
    <style jsx>{`
      .tooltip-container {
        display: flex;
        align-items: center;
        padding: 3px 10px;
        font-size: 16px;
        font-weight: 700;
        line-height: 170%;
      }
    `}</style>
  </>
);

function getYadoLink(yadoId: string, searchCondition: SearchCondition, soldOut?: boolean) {
  const linkParams: {
    yadoId: string;
    searchCondition?: SearchCondition;
  } = { yadoId };
  if (!soldOut) {
    linkParams.searchCondition = searchCondition;
  }
  return createOnsenYadoDetailLink(linkParams);
}

interface YadosMapPopupProps {
  hotel: YadoDetailJson;
  yadoPrice?: YadoPrice | null;
  searchCondition: SearchCondition;
  currency?: CurrencyCode;
  couponStore?: CouponStore;
  hideDetail?: boolean;
  type?: 'link' | 'button';
  setCenter?: () => void;
  setActive?: () => void;
  removeActive?: () => void;
  withChevron?: boolean;
}

export const YadosMapPopup = ({
  type = 'link',
  hotel,
  searchCondition,
  yadoPrice,
  currency,
  hideDetail,
  setCenter,
  setActive,
  removeActive,
  withChevron,
}: YadosMapPopupProps) => {
  const { id, localizedName, rating } = hotel;
  const priceIncludingTax = yadoPrice?.priceIncludingTax;
  const { totalAdults, totalChildren } = searchCondition;

  const yadoLink = getYadoLink(id, searchCondition, yadoPrice?.isSoldOut);

  const { isMobile } = useContext(DeviceContext);

  const Wrapper = ({ children }: { children: ReactNode }) => {
    if (type === 'link') {
      return (
        <Link className="link-wrapper" {...yadoLink} target="_blank">
          {children}
        </Link>
      );
    }
    return (
      <div
        className="link-wrapper cursor-pointer"
        onClick={setCenter}
        onMouseEnter={setActive}
        onMouseLeave={removeActive}
      >
        {children}
      </div>
    );
  };

  return (
    <>
      <Wrapper>
        <ImageLazy
          src={hotel.images[0]}
          alt={hotel.name || hotel.japaneseName || ''}
          style={{ width: 150 }}
        />
        <div className="top-content">
          <div className="yado-left-container">
            <h2 className="name mb-0">{localizedName}</h2>
            <div className="rate-wrapper">
              {rating.total && (
                <div className="rate">
                  <span className="rate-number">{rating.total}/10</span>
                  {evaluationLabel(Number(rating.total))}
                </div>
              )}
            </div>
          </div>
          <div className="yado-right-container">
            <div className="text-right">
              <div className="price-wrapper">
                {hideDetail ? (
                  <>
                    {priceIncludingTax && (
                      <div className="d-flex justify-content-end my-1">
                        <Price
                          priceJpy={
                            !yadoPrice?.discountedPriceIncludingTax
                              ? priceIncludingTax
                              : yadoPrice?.discountedPriceIncludingTax
                          }
                          exchange
                          size="md"
                          showCurrencyName={false}
                          fontWeight="bold"
                        />
                        <div className="price-info">{`${currency}\n${i18n.webYadoTaxIncluded()}`}</div>
                      </div>
                    )}
                    {yadoPrice?.isSoldOut && (
                      <div className="hide-detail-soldout-text">
                        {i18n.webYadoRoomsSoldOutOnSelectedDate()}
                      </div>
                    )}
                    <div className="d-flex justify-content-end">
                      <Link className="link-wrapper to-detail-btn" {...yadoLink} target="_blank">
                        {i18n.webYadoSeeDetail()}
                      </Link>
                    </div>
                  </>
                ) : (
                  <>
                    {priceIncludingTax && (
                      <>
                        {yadoPrice?.discountedPriceIncludingTax && isMobile.phone && (
                          <div className="justify-content-start discount-price-mobile">
                            <div className="d-flex justify-content-end">
                              <p className="coupon-text mb-1">
                                {i18n.webYadoCouponAppliedText()}
                                {'\u00A0'}
                              </p>
                              <Price
                                priceJpy={priceIncludingTax}
                                exchange
                                size="xs"
                                showCurrencyName={false}
                                strikeThrough
                                strikeThroughLineColor="#545454"
                              />
                              <span className="discount-currency">{`${'\u00A0'}${currency}`}</span>
                            </div>
                          </div>
                        )}
                        <div className="my-1">
                          <PriceJpy
                            price={
                              !yadoPrice?.discountedPriceIncludingTax
                                ? priceIncludingTax
                                : yadoPrice?.discountedPriceIncludingTax
                            }
                          />
                        </div>
                        <div className="price-parallel">
                          {yadoPrice?.discountedPriceIncludingTax && !isMobile.phone && (
                            <div className="justify-content-start discount-price">
                              <p className="coupon-text mb-1">{i18n.webYadoCouponAppliedText()}</p>
                              <div className="d-flex justify-content-end">
                                <Price
                                  priceJpy={priceIncludingTax}
                                  exchange
                                  size="xs"
                                  showCurrencyName={false}
                                  strikeThrough
                                  strikeThroughLineColor="#545454"
                                />
                                <span className="discount-currency">{`${'\u00A0'}${currency}`}</span>
                              </div>
                            </div>
                          )}
                          <div className="d-flex justify-content-end">
                            <Price
                              priceJpy={
                                !yadoPrice?.discountedPriceIncludingTax
                                  ? priceIncludingTax
                                  : yadoPrice?.discountedPriceIncludingTax
                              }
                              exchange
                              size="md"
                              showCurrencyName={false}
                              fontWeight="bold"
                            />
                            <div className="price-info">{`${currency}\n${i18n.webYadoTaxIncluded()}`}</div>
                          </div>
                        </div>
                        <div className="black-text mt-1">
                          {i18n.yadoAdultWithNumLabel({ count: totalAdults })}
                          {totalChildren >= 1 &&
                            `${i18n.webYadoDelimiter()}${i18n.yadoChildrenWithNumLabel({
                              count: totalChildren,
                            })}`}
                          <br />
                          {i18n.webYadoLowestPriceForOneDay()}
                        </div>
                      </>
                    )}
                    {yadoPrice?.isSoldOut && (
                      <div>
                        <SoldOut />
                      </div>
                    )}
                    {yadoPrice?.isLoading && <div>Loading...</div>}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
        {withChevron && (
          <div className="popup-chevron-container">
            <FontAwesomeIcon icon={faChevronRight} size="sm" color={Colors.gray600} />
          </div>
        )}
      </Wrapper>
      <style jsx>{`
        .yado-right-container :global(.coupon-label) {
          font-size: 0.6875rem;
          font-weight: 500;
          margin-top: 2px;
        }
        :global(.link-wrapper) {
          color: ${Colors.textDarkGray} !important;
          text-decoration: none !important;
          display: flex;
          width: 100%;
          padding-right: 15px;
        }
        :global(.link-wrapper button) {
          background-color: transparent;
          border: none;
          width: 100%;
        }
        :global(.link-wrapper button:focus) {
          outline: none;
        }
        :global(.to-detail-btn) {
          width: auto;
          background-color: ${Colors.pjtBlue};
          padding: 10px 20px;
          border-radius: 3px;
          border: 1px solid ${Colors.pjtBlue};
          color: white !important;
          font-size: 15px;
          font-weight: 700;
        }
        .black-text {
          color: ${Colors.textDarkGray};
          font-size: 0.6875rem;
          white-space: pre-wrap;
        }
        .name {
          font-size: 1rem;
          font-weight: 700;
        }
        .rate-wrapper {
          font-size: 13px;
          margin-top: 5px;
          display: flex;
        }
        .rate {
          margin-right: 10px;
        }
        .rate-number {
          font-weight: bold;
          margin-right: 3px;
        }
        .price-info {
          white-space: pre-wrap;
          font-size: 10px;
          font-weight: bold;
          line-height: 12px;
          padding-left: 5px;
          color: ${Colors.textDarkGray};
          display: flex;
        }
        .coupon-text {
          color: ${Colors.onsenRed};
          font-size: 12px;
          line-height: 16px;
          text-align: right;
          width: 100px;
        }
        .discount-currency {
          color: ${Colors.onsenGray800};
          font-size: 12px;
          font-weight: bold;
          text-decoration: line-through solid;
          line-height: 17px;
        }
        .rating {
          width: auto;
        }
        .location {
          color: ${Colors.textDarkGray};
          font-size: 12px;
        }
        .rating-number {
          font-weight: 600;
          font-size: 18px;
          color: ${Colors.onsenPink};
          letter-spacing: 0;
          text-align: center;
        }
        .coupon-label {
          font-size: 8px;
          margin-right: 5px;
          line-height: 8px;
          color: ${Colors.onsenRed};
        }
        .yado-left-container {
          flex: 1 1 50%;
          width: 100%;
          margin-top: 10px;
        }
        .yado-right-container {
          flex: 1;
          width: 100%;
          min-width: 140px;
        }
        .top-content {
          margin-left: 13px;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          width: 100%;
          padding-bottom: 10px;
        }
        .hide-detail-soldout-text {
          color: ${Colors.textDarkGray};
          text-align: right;
          font-size: 13px;
          font-weight: 400;
          margin-bottom: 3px;
        }
        .popup-chevron-container {
          display: flex;
          align-items: center;
          margin-left: 10px;
          margin-right: 5px;
        }
        @media screen and (min-width: ${Breakpoints.lgMin}) {
          .name {
            font-size: 1.0625rem;
          }
          .price-wrapper {
            margin-top: 10px;
          }
          .yado-right-container {
            min-width: 222px;
            flex: 0;
          }
          .price-parallel {
            display: flex;
            justify-content: flex-end;
            gap: 20px;
          }
          .discount-price {
            position: relative;
            top: -14px;
          }
        }
      `}</style>
    </>
  );
};

interface YadosMapPopupListProps {
  popups: Array<{ element: ReactNode; id: string }>;
  activeIndex?: ActivePointContent;
}

const popupGap = 15;

export const YadosMapPopupList = ({ popups, activeIndex }: YadosMapPopupListProps) => {
  const popupRefs = popups.map(() => createRef<HTMLDivElement>());
  const containerRef = useRef<HTMLDivElement>(null);
  const prevActiveRef = useRef(activeIndex);

  useEffect(() => {
    if (activeIndex && activeIndex.index !== prevActiveRef.current?.index) {
      const target = popupRefs[activeIndex.index].current;
      if (containerRef.current && target && activeIndex.ableToScroll) {
        containerRef.current.scrollTo({
          top: target.offsetTop - popupGap,
          behavior: 'smooth',
        });
      }
    }
    prevActiveRef.current = activeIndex;
  }, [activeIndex, popupRefs]);

  return (
    <>
      <div className="list-container" ref={containerRef}>
        {popups.map((popup, i) => {
          if (!popup.element) {
            return null;
          }
          return (
            <div
              className={classNames('popup-wrapper', { active: activeIndex?.index === i })}
              ref={popupRefs[i]}
              key={popup.id}
            >
              {popup.element}
            </div>
          );
        })}
      </div>
      <style jsx>{`
        .list-container {
          height: 100%;
          overflow-y: scroll;
          position: relative;
        }
        .list-container::-webkit-scrollbar {
          display: none;
        }
        .popup-wrapper {
          margin: ${popupGap}px 10px 0;
          background-color: ${Colors.onsenWhite};
          border: 1px solid ${Colors.gray300};
          border-radius: 3px;
          overflow: hidden;
        }
        .popup-wrapper:last-child {
          margin-bottom: ${popupGap}px;
        }
        .popup-wrapper.active {
          background-color: ${Colors.onsenLightBlue};
          box-shadow: 0px 0px 15px 0px rgba(0, 0, 0, 0.25);
        }
      `}</style>
    </>
  );
};

const starSize = 15;
const RatingStars = ({ rating }: { rating: number }) => (
  <>
    {new Array(5).fill(0).map((_item, i) => (
      <NextImage
        width={starSize}
        height={starSize}
        containerStyle={{ backgroundColor: 'transparent', width: 'auto', marginRight: 5 }}
        src={
          i + 1 <= Math.round(rating / 2)
            ? R.assets.images.commonStarYellow
            : R.assets.images.commonStarWhite
        }
        alt="star"
        key={`star-${i * i}`}
      />
    ))}
  </>
);

interface YadoModalIntroductionProps {
  hotel: YadoDetailJson;
  closeModal: () => void;
}

const yadoModalCloseBtnSize = 40;

export const YadoModalIntroduction = ({ hotel, closeModal }: YadoModalIntroductionProps) => {
  const { localizedName, japaneseName, rating } = hotel;
  return (
    <>
      <div>
        <div className="detail-modal-container">
          <div className="hotel-content">
            {localizedName && <div className="local-name">{localizedName}</div>}
            {japaneseName && <div className="jpn-name">{japaneseName}</div>}
            {rating.total && (
              <div className="rating-container">
                <RatingStars rating={Number(rating.total)} />
                <span className="rating-number">{rating.total}</span>
                <span className="comment">{evaluationLabel(Number(rating.total))}</span>
              </div>
            )}
          </div>
          <button onClick={closeModal} type="button" className="close-btn">
            <NextImage
              src={R.assets.images.commonCloseBtnGray}
              width={yadoModalCloseBtnSize}
              height={yadoModalCloseBtnSize}
              containerStyle={{
                backgroundColor: 'transparent',
                width: yadoModalCloseBtnSize,
                height: yadoModalCloseBtnSize,
              }}
              alt=""
            />
          </button>
        </div>
        <div className="nearbys-title">{i18n.nearbyHotels()}</div>
      </div>
      <style jsx>{`
        .detail-modal-container {
          display: flex;
          width: 100%;
          justify-content: space-between;
        }
        .hotel-content {
          flex-grow: 1;
        }
        .rating-container {
          display: flex;
          padding: 10px 0 24px;
          align-items: center;
        }
        .local-name {
          color: ${Colors.gray400};
          font-size: 14px;
          font-weight: 400;
        }
        .jpn-name {
          color: ${Colors.textDarkGray};
          font-size: 28px;
          font-weight: 700;
        }
        .close-btn {
          width: 40px;
          height: 40px;
          border: 0;
          outline: none;
          background-color: transparent;
          border-radius: 50%;
        }
        .rating-number {
          color: ${Colors.onsenDarkYellow};
          font-size: 15px;
          font-weight: 700;
        }
        .comment {
          font-size: 14px;
          font-weight: 400;
          margin-left: 7px;
        }
      `}</style>
    </>
  );
};
